import Layout from '../components/layout/Layout';
import SectionLabel from '../components/shared/SectionLabel';
import SEO from '../components/shared/SEO';
import '../styles/riskdisclosuresummary.css';

const keyFacts = [
  { label: 'Issuer', value: 'Apex DeFi Labs Inc., a Delaware corporation' },
  { label: 'Platform', value: 'Veep Capital — VYASA private capital markets platform (veepcapital.com)' },
  {
    label: 'Offering type',
    value: 'Private placement of unregistered securities; exempt from registration under the Securities Act of 1933',
  },
  { label: 'Eligible investors', value: 'Verified accredited investors only (Rule 501(a) of Regulation D)' },
  { label: 'Minimum investment', value: '$10,000' },
  { label: 'Version', value: 'September 2026 — DRAFT FOR COUNSEL REVIEW' },
];

const documentClassification = 'APEX DEFI LABS INC. · RISK DISCLOSURE SUMMARY · CONFIDENTIAL';
const documentFooterNotice = '© 2026 Apex DeFi Labs Inc. Not for public distribution.';

const sections = [
  {
    id: 'purpose-and-scope',
    number: '01',
    heading: 'Purpose and Scope of This Summary',
    body: (
      <>
        <p>
          This Risk Disclosure Summary is provided to prospective investors who have received an invitation to
          consider an investment in Apex DeFi Labs Inc. (the &ldquo;Company,&rdquo; &ldquo;Apex,&rdquo;
          &ldquo;we,&rdquo; or &ldquo;us&rdquo;). It is intended to help you understand the nature of the
          investment and the principal risks involved before you review the Offering Documents.
        </p>
        <p>
          Nothing in this summary is an offer to sell or a solicitation of an offer to buy any security. An
          offer may only be made through the Offering Documents to persons who have been verified as
          accredited investors. This summary does not constitute legal, tax, accounting, or investment advice.
          You should consult your own advisors before investing.
        </p>
      </>
    ),
  },
  {
    id: 'nature-of-offering',
    number: '02',
    heading: 'Nature of the Offering',
    body: (
      <ul>
        <li>
          <strong>Unregistered securities.</strong> The securities have not been registered under the
          Securities Act of 1933 or the securities laws of any state, and are offered in reliance on an
          exemption from registration. Neither the SEC nor any state securities regulator has reviewed,
          approved, or passed upon the merits of this offering or the accuracy of these materials. Any
          representation to the contrary is a criminal offense.
        </li>
        <li>
          <strong>Accredited investors only.</strong> Participation is limited to persons who qualify as
          accredited investors and whose status has been verified through the VYASA platform prior to
          acceptance of any subscription. The Company may reject any subscription in whole or in part.
        </li>
        <li>
          <strong>Digital record of ownership.</strong> Ownership of the securities may be recorded on a
          blockchain-based ledger (&ldquo;Apex Token&rdquo;). The token is a digital representation of a
          conventional security issued by the Company; it is not a cryptocurrency, utility token, or
          stand-alone digital asset, and it confers only the rights set out in the Offering Documents.
        </li>
        <li>
          <strong>Offering price set by the Company.</strong> The price of the securities has been determined
          by the Company and does not necessarily reflect the Company&rsquo;s book value, earnings, or an
          independent appraisal. There is no assurance that the securities could be resold at the offering
          price or at all.
        </li>
        <li>
          <strong>Use of proceeds.</strong> Proceeds will be applied at the discretion of management as
          described in the Offering Documents. Management will have broad discretion in applying proceeds, and
          you will be relying on management&rsquo;s judgment.
        </li>
      </ul>
    ),
  },
  {
    id: 'company-and-business-risks',
    number: '03',
    heading: 'Risks Related to the Company and Its Business',
    body: (
      <ul>
        <li>
          <strong>Early-stage company; limited operating history.</strong> The Company is at an early stage of
          development and has a limited operating history on which to evaluate its prospects. It may not
          achieve or sustain revenue or profitability. Many early-stage companies fail, and the
          Company&rsquo;s business plan may not succeed.
        </li>
        <li>
          <strong>Need for additional capital.</strong> The Company expects to require substantial additional
          capital to execute its plans. Additional financing may not be available on acceptable terms or at
          all. Future financings may be on terms senior to, or that dilute, your investment. If capital is
          unavailable, the Company may have to curtail or cease operations.
        </li>
        <li>
          <strong>Dependence on key personnel.</strong> The Company depends on its founders and a small
          leadership team across the United States and India. Loss of key personnel, or an inability to
          attract and retain qualified staff, could materially harm the business.
        </li>
        <li>
          <strong>Competition.</strong> The Company operates in a competitive and rapidly evolving market that
          includes established financial institutions, exchanges, and well-capitalized technology companies.
          Competitors may have greater resources, brand recognition, regulatory licenses, and market access
          than the Company.
        </li>
        <li>
          <strong>Multi-jurisdictional operations.</strong> The Company conducts operations through affiliated
          entities in the United States and India. It is exposed to differing legal, tax, foreign-exchange,
          data-protection, and employment regimes, and to cross-border capital-flow restrictions, any of which
          could increase costs or restrict operations.
        </li>
        <li>
          <strong>Reliance on third parties.</strong> The Company relies on third-party service providers,
          including broker-dealers, escrow agents, transfer agents, IRA custodians, cloud infrastructure
          providers, and blockchain networks. Failure, termination, or non-performance by any of these parties
          could disrupt the business.
        </li>
        <li>
          <strong>Intellectual property.</strong> The Company&rsquo;s ability to protect its technology and
          brands is uncertain. Third parties may assert infringement claims, and the Company may be unable to
          obtain or enforce patents, trademarks, or other rights.
        </li>
      </ul>
    ),
  },
  {
    id: 'regulatory-and-legal-risks',
    number: '04',
    heading: 'Regulatory and Legal Risks',
    body: (
      <ul>
        <li>
          <strong>Evolving regulation of digital securities and fintech.</strong> The regulatory treatment of
          blockchain-based securities, digital asset infrastructure, stablecoins, and private capital markets
          technology is developing and subject to change at the federal and state levels and internationally.
          New legislation, rulemaking, interpretations, or enforcement actions could restrict the
          Company&rsquo;s products, increase compliance costs, require licenses the Company does not hold, or
          render aspects of its business model unlawful or uneconomic.
        </li>
        <li>
          <strong>Legislative references are context only.</strong> References in the Company&rsquo;s
          materials to recent legislation, including the GENIUS Act, or to initiatives by exchanges or
          financial institutions, describe market context. They are not assurances that such developments
          will benefit the Company, and the implementation, scope, and effect of any such legislation remain
          uncertain.
        </li>
        <li>
          <strong>Licensing and registration status.</strong> Neither the Company nor the Veep Capital
          platform is registered as a broker-dealer, investment adviser, exchange, alternative trading system,
          or transfer agent. Investor solicitation and transaction execution, where required, are conducted
          through separately engaged FINRA-registered broker-dealers and other licensed providers. If
          regulators determine that any activity of the Company requires registration or licensing that it
          does not hold, the Company could face penalties, rescission obligations, or restrictions on its
          business.
        </li>
        <li>
          <strong>Compliance with the exemption.</strong> If the Company fails to comply with the conditions
          of the exemption relied upon for this offering, investors may have rescission rights and the Company
          could be subject to regulatory action. The Company&rsquo;s ability to conduct future exempt
          offerings could also be impaired.
        </li>
        <li>
          <strong>Securities law compliance of the platform business.</strong> The Company&rsquo;s platform
          serves issuers conducting their own exempt offerings. Non-compliance by issuers or partners, or
          changes in how regulators view technology platforms in private capital markets, could expose the
          Company to liability or reputational harm.
        </li>
        <li>
          <strong>Data privacy and cybersecurity law.</strong> The Company handles personal and financial data
          subject to U.S. and Indian data-protection regimes. Non-compliance or a data breach could result in
          fines, litigation, and loss of customer trust.
        </li>
        <li>
          <strong>Litigation.</strong> The Company may become subject to claims, disputes, or regulatory
          inquiries. Litigation is costly, distracting, and unpredictable regardless of merit.
        </li>
      </ul>
    ),
  },
  {
    id: 'illiquidity-and-transfer-restrictions',
    number: '05',
    heading: 'Illiquidity and Transfer Restrictions',
    body: (
      <ul>
        <li>
          <strong>No public market.</strong> There is no public trading market for the Company&rsquo;s
          securities, and none is expected to develop. The Company has no obligation to list its securities on
          any exchange or alternative trading system, and no assurance can be given that any listing,
          liquidity event, sale of the Company, or public offering will occur.
        </li>
        <li>
          <strong>Restricted securities.</strong> The securities are &ldquo;restricted securities&rdquo; under
          Rule 144. They may not be resold unless registered or an exemption from registration is available.
          Rule 144 generally requires a minimum one-year holding period for securities of a non-reporting
          issuer, and may impose additional conditions. Resale may also be restricted under the Company&rsquo;s
          governing documents, including rights of first refusal and transfer-approval requirements.
        </li>
        <li>
          <strong>Indefinite holding period.</strong> You should be prepared to hold your investment
          indefinitely. You may be unable to sell when you wish to, or at a price you consider fair.
        </li>
        <li>
          <strong>Platform-facilitated secondary transfers are not assured.</strong> Any secondary transfer
          functionality on the VYASA platform, if and when available, will be subject to regulatory
          requirements, the involvement of licensed intermediaries, and the Company&rsquo;s transfer
          restrictions. Availability of such functionality does not guarantee that a buyer will exist.
        </li>
        <li>
          <strong>No redemption right.</strong> The Company is under no obligation to repurchase or redeem
          your securities.
        </li>
      </ul>
    ),
  },
  {
    id: 'dilution-capital-structure-and-investor-rights',
    number: '06',
    heading: 'Dilution, Capital Structure, and Investor Rights',
    body: (
      <ul>
        <li>
          <strong>Dilution.</strong> The Company expects to issue additional equity in future financings, to
          employees and advisors under equity incentive arrangements, and in connection with acquisitions or
          partnerships. Each such issuance will dilute your percentage ownership and may be at a lower price
          than you paid.
        </li>
        <li>
          <strong>Founder control and limited voting rights.</strong> The Company&rsquo;s capital structure
          includes classes of stock with differential voting rights, and its founders retain voting control.
          The securities offered may carry limited or no voting rights. You will have little or no ability to
          influence corporate decisions, including decisions about additional financings, changes of control,
          or the sale of the Company.
        </li>
        <li>
          <strong>Subordination.</strong> In a liquidation, holders of debt and any senior preferred equity
          would be paid before holders of the securities offered. There may be no assets remaining for
          distribution to you.
        </li>
        <li>
          <strong>No dividends expected.</strong> The Company does not anticipate paying dividends or
          distributions for the foreseeable future. Any return on investment would depend on a future
          liquidity event that may never occur.
        </li>
        <li>
          <strong>Limited information rights.</strong> As a private company, Apex is not subject to the
          periodic reporting requirements of the Securities Exchange Act of 1934. You will receive only the
          information the Company is contractually obligated or elects to provide. The Company&rsquo;s
          financial statements may be unaudited.
        </li>
      </ul>
    ),
  },
  {
    id: 'technology-and-blockchain-risks',
    number: '07',
    heading: 'Technology and Blockchain Risks',
    body: (
      <ul>
        <li>
          <strong>Smart-contract and software risk.</strong> Ownership records and transfer logic may be
          implemented in smart contracts and platform software. Defects, vulnerabilities, or unintended
          behavior in this code could result in loss, freezing, mis-recording, or unauthorized transfer of
          securities, and may be difficult or impossible to reverse.
        </li>
        <li>
          <strong>Cybersecurity.</strong> The platform and its service providers may be targeted by hacking,
          phishing, malware, or other attacks. A successful attack could compromise investor data, disrupt
          operations, or result in loss of assets.
        </li>
        <li>
          <strong>Blockchain network risk.</strong> The Company does not control the blockchain networks it
          may use. Network congestion, forks, protocol changes, validator or miner behavior, or the failure of
          a network could impair the Company&rsquo;s ability to maintain accurate records or process
          transfers.
        </li>
        <li>
          <strong>Key management and access.</strong> Access to digital records may depend on private keys,
          credentials, or custodial arrangements. Loss, theft, or compromise of keys or credentials, whether
          by you, the Company, or a custodian, could result in permanent loss of access to your securities.
        </li>
        <li>
          <strong>Technology obsolescence.</strong> Rapid technological change may render the Company&rsquo;s
          platform or its chosen infrastructure obsolete or non-competitive.
        </li>
        <li>
          <strong>Reliance on the Company&rsquo;s own platform.</strong> The Company is the parent of the
          platform on which the securities are recorded and administered. Operational failure of the platform
          would affect both the Company&rsquo;s business and the administration of your investment.
        </li>
      </ul>
    ),
  },
  {
    id: 'ira-investment-risks',
    number: '08',
    heading: 'Risks Specific to Investing Through an IRA',
    body: (
      <>
        <p>
          The Company has arranged for investors to participate through self-directed individual retirement
          accounts held with a third-party custodian. The custodian is an independent company that acts as a
          passive custodian only; it does not evaluate, endorse, or recommend this investment and provides no
          investment, legal, or tax advice.
        </p>
        <ul>
          <li>
            <strong>Tax treatment is not guaranteed.</strong> Whether gains in a Roth IRA are tax-free, or
            growth in a Traditional IRA is tax-deferred, depends on your eligibility, contribution limits,
            holding periods, age, and continued compliance with Internal Revenue Code requirements. Statements
            that gains &ldquo;may be&rdquo; tax-free are conditional and are not a promise of any particular
            tax outcome.
          </li>
          <li>
            <strong>Prohibited transactions.</strong> IRA rules prohibit certain transactions between an IRA
            and &ldquo;disqualified persons,&rdquo; including the account holder and certain family members
            and affiliated entities. A prohibited transaction can cause the entire IRA to lose its
            tax-advantaged status, with immediate taxation and penalties.
          </li>
          <li>
            <strong>Unrelated business taxable income (UBTI).</strong> Depending on the structure of the
            investment and the Company&rsquo;s activities, an IRA may incur UBTI or unrelated debt-financed
            income, which is taxable within the IRA and may require the IRA to file a tax return.
          </li>
          <li>
            <strong>Liquidity for required distributions.</strong> Because the investment is illiquid, an IRA
            holding it may lack sufficient liquid assets to satisfy required minimum distributions or other
            withdrawals, potentially resulting in penalties or a forced in-kind distribution.
          </li>
          <li>
            <strong>Valuation reporting.</strong> Custodians require an annual fair market value for illiquid
            assets. Valuations of private securities are inherently uncertain and may be provided by the
            Company; they are not independent appraisals.
          </li>
          <li>
            <strong>Custodian fees and administration.</strong> Self-directed IRA custodians charge
            account-opening, annual, transaction, and asset-holding fees, which reduce returns. Delays in
            custodian processing may affect the timing of your subscription.
          </li>
          <li>
            <strong>Concentration and suitability.</strong> Retirement assets are generally intended for
            long-term security. Allocating a significant portion of retirement savings to a single
            speculative, illiquid private investment may be unsuitable for you. Consult a qualified tax and
            financial advisor before proceeding.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'conflicts-of-interest',
    number: '09',
    heading: 'Conflicts of Interest',
    body: (
      <ul>
        <li>
          <strong>Issuer and platform affiliation.</strong> Apex DeFi Labs Inc. is the issuer of the
          securities and the parent of the entities that operate the Veep Capital VYASA platform. The platform
          is therefore not an independent intermediary with respect to this offering, and the Company has an
          interest in the offering&rsquo;s success that may differ from your interests.
        </li>
        <li>
          <strong>Management compensation and related-party arrangements.</strong> Officers and directors are
          compensated by the Company and may hold securities on terms different from yours. The Company may
          enter into transactions with affiliated entities in the United States and India.
        </li>
        <li>
          <strong>Other business activities.</strong> Management is engaged in multiple ventures and will not
          devote their full time exclusively to any single business line.
        </li>
        <li>
          <strong>Compensation of intermediaries.</strong> Broker-dealers, referral partners, or other
          intermediaries involved in the offering may receive compensation, which will be disclosed in the
          Offering Documents. Such compensation reduces the proceeds available to the Company.
        </li>
      </ul>
    ),
  },
  {
    id: 'no-endorsement',
    number: '10',
    heading: 'No Endorsement; Third-Party References',
    body: (
      <>
        <p>
          The Company&rsquo;s marketing materials refer to institutions such as the New York Stock Exchange,
          Nasdaq, BlackRock, and JPMorgan, and to public policy developments, to describe the broader market
          environment in which the Company operates. None of these institutions is affiliated with the
          Company, has invested in the Company, has partnered with the Company, or has reviewed or endorsed
          this offering. Market developments affecting large institutions do not indicate that the Company
          will benefit from them or that its securities will appreciate in value.
        </p>
        <p>
          References to the Company&rsquo;s IRA custodian relationship, technology vendors, or professional
          advisors identify service providers only and do not constitute an endorsement of this investment by
          any such party.
        </p>
      </>
    ),
  },
  {
    id: 'forward-looking-statements',
    number: '11',
    heading: 'Forward-Looking Statements',
    body: (
      <p>
        The Company&rsquo;s materials contain forward-looking statements, including statements about market
        opportunity, regulatory developments, product roadmaps, expected growth, potential liquidity events,
        and future performance. These statements are based on current expectations and assumptions that are
        subject to significant business, economic, regulatory, and competitive uncertainties, many of which
        are beyond the Company&rsquo;s control. Actual results may differ materially. Any projections or
        targets are illustrative only, are not guarantees, and should not be relied upon as a prediction of
        future results. The Company undertakes no obligation to update any forward-looking statement.
      </p>
    ),
  },
  {
    id: 'tax-considerations',
    number: '12',
    heading: 'Tax Considerations',
    body: (
      <p>
        The tax consequences of acquiring, holding, and disposing of the securities are complex and depend on
        your individual circumstances, your jurisdiction, and the account through which you invest. Tax laws
        and their interpretation may change, possibly with retroactive effect. The Company does not provide
        tax advice and makes no representation regarding the tax treatment of any investment. You should
        consult your own tax advisor.
      </p>
    ),
  },
  {
    id: 'investor-verification-data-and-communications',
    number: '13',
    heading: 'Investor Verification, Data, and Communications',
    body: (
      <ul>
        <li>
          <strong>Verification.</strong> You will be required to provide documentation supporting your
          accredited investor status and identity, and to complete anti-money-laundering and sanctions
          screening, through the VYASA platform or a third-party verification provider before your
          subscription can be accepted.
        </li>
        <li>
          <strong>Data handling.</strong> Information you provide will be processed by the Company, the
          platform operator, and their service providers in accordance with the applicable privacy policy.
          Although reasonable safeguards are used, no system is fully secure.
        </li>
        <li>
          <strong>Electronic delivery.</strong> By participating, you consent to receive offering materials,
          notices, and account communications electronically.
        </li>
        <li>
          <strong>Confidentiality.</strong> The invitation you received and the Offering Documents are
          confidential and may not be forwarded, reproduced, or distributed to any other person.
        </li>
      </ul>
    ),
  },
  {
    id: 'investor-acknowledgments',
    number: '14',
    heading: 'Investor Acknowledgments',
    body: (
      <>
        <p>
          By proceeding to review the Offering Documents and submit a subscription, you will be asked to
          confirm that you:
        </p>
        <ul>
          <li>Have read and understood this Risk Disclosure Summary and the Offering Documents in their entirety;</li>
          <li>Are an accredited investor and have provided, or will provide, verification of that status;</li>
          <li>Can bear the economic risk of losing your entire investment and can hold it for an indefinite period;</li>
          <li>
            Have had the opportunity to ask questions of the Company and to obtain any additional information
            you consider necessary;
          </li>
          <li>
            Have relied solely on the Offering Documents, and not on any marketing material, verbal statement,
            or third-party reference, in making your investment decision;
          </li>
          <li>Have consulted, or had the opportunity to consult, your own legal, tax, and financial advisors; and</li>
          <li>
            Understand that neither the Company nor the Veep Capital platform has provided investment, legal,
            or tax advice or a recommendation regarding the suitability of this investment for you.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'how-to-obtain-further-information',
    number: '15',
    heading: 'How to Obtain Further Information',
    body: (
      <>
        <p>
          The full Offering Documents are available to verified accredited investors through the VYASA
          platform at veepcapital.com. Questions regarding this summary or the offering may be directed to
          Apex DeFi Labs Inc., 244 Fifth Avenue, Suite V255, New York, NY 10001, or to info@apexdefilabs.com.
        </p>
        <p>
          This summary is dated September 2026. Neither its delivery nor any sale of securities implies that
          the information it contains is correct as of any later date.
        </p>
      </>
    ),
  },
];

const tocLinks = sections.map((section) => ({ label: section.heading, id: section.id }));

export default function RiskDisclosureSummaryPage() {
  return (
    <Layout>
      <SEO
        title="Risk Disclosure Summary - Apex DeFi Labs"
        description="Read the Apex DeFi Labs Risk Disclosure Summary covering the nature of the offering, company and regulatory risks, illiquidity, and investor acknowledgments."
        url="https://apexdefilabs.com/risk-disclosure-summary"
        type="website"
        keywords="Apex DeFi Labs risk disclosure summary, private placement risk, accredited investor, regulation D"
      />

      <div className="risk-disclosure-page">
        {/* Hero */}
        <section className="risk-disclosure-hero">
          <div className="risk-disclosure-hero-overlay" />
          <div className="risk-disclosure-classification">{documentClassification}</div>
          <div className="risk-disclosure-shell">
            <div style={{ marginBottom: '1.2rem' }}>
              <SectionLabel>Legal</SectionLabel>
            </div>
            <h1 className="risk-disclosure-hero-title">
              <em>Risk Disclosure Summary</em>
            </h1>
            <p className="risk-disclosure-hero-subtitle">
              APEX DEFI LABS INC. · PRIVATE INVESTMENT OPPORTUNITY
            </p>
            <p className="risk-disclosure-hero-lede">
              Securities of Apex DeFi Labs Inc. offered to verified accredited investors through the Veep
              Capital VYASA platform
            </p>
          </div>
        </section>

        {/* Content */}
        <section className="risk-disclosure-content">
          <div className="risk-disclosure-shell">
            {/* Key Facts */}
            <div className="risk-disclosure-facts">
              {keyFacts.map((fact) => (
                <div key={fact.label} className="risk-disclosure-fact">
                  <span className="risk-disclosure-fact-label">{fact.label}</span>
                  <span className="risk-disclosure-fact-value">{fact.value}</span>
                </div>
              ))}
            </div>

            {/* Read This Before You Invest */}
            <div className="risk-disclosure-callout risk-disclosure-callout--invest">
              <div className="risk-disclosure-callout-title">Read This Before You Invest</div>
              <p>
                An investment in Apex DeFi Labs Inc. is speculative, illiquid, and involves a high degree of
                risk, including the possible loss of your entire investment. You should invest only if you can
                afford to lose all of the money you invest and can hold the investment for an indefinite
                period.
              </p>
              <p>
                This summary highlights principal risks. It is not complete. The Private Placement Memorandum,
                Subscription Agreement, and the Company&rsquo;s governing documents (together, the
                &ldquo;Offering Documents&rdquo;) contain additional risk factors and govern in the event of
                any inconsistency with this summary.
              </p>
            </div>

            {/* Table of Contents */}
            <nav className="risk-disclosure-toc" aria-label="Table of contents">
              <div className="risk-disclosure-toc-title">Contents</div>
              <ol className="risk-disclosure-toc-list">
                {tocLinks.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>{item.label}</a>
                  </li>
                ))}
              </ol>
            </nav>

            {/* Sections */}
            {sections.map((section) => (
              <article key={section.id} id={section.id} className="risk-disclosure-section">
                <span className="risk-disclosure-section-number">{section.number}</span>
                <h2 className="risk-disclosure-section-heading">{section.heading}</h2>
                <div className="risk-disclosure-section-body">{section.body}</div>
              </article>
            ))}

            {/* Important Notice */}
            <div className="risk-disclosure-callout risk-disclosure-callout--notice">
              <div className="risk-disclosure-callout-title">Important Notice</div>
              <p>
                THE SECURITIES DESCRIBED HEREIN HAVE NOT BEEN REGISTERED UNDER THE SECURITIES ACT OF 1933, AS
                AMENDED, OR ANY STATE SECURITIES LAWS, AND MAY NOT BE OFFERED, SOLD, OR TRANSFERRED EXCEPT
                PURSUANT TO REGISTRATION OR AN APPLICABLE EXEMPTION. THE SECURITIES ARE SUBJECT TO RESTRICTIONS
                ON TRANSFERABILITY AND RESALE. INVESTORS SHOULD BE AWARE THAT THEY MAY BE REQUIRED TO BEAR THE
                FINANCIAL RISKS OF THIS INVESTMENT FOR AN INDEFINITE PERIOD OF TIME.
              </p>
              <p>
                This document is a summary prepared by the Company for informational purposes. It is not a
                prospectus, offering memorandum, or offering circular. It has not been reviewed by any
                regulator. This draft is subject to review by the Company&rsquo;s securities counsel and,
                where required, by a registered principal of the engaged broker-dealer prior to distribution.
              </p>
            </div>

            {/* Document footer notice */}
            <div className="risk-disclosure-footnote">{documentFooterNotice}</div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
